import type { LedgerEvent, SubscriptionResponse } from '@talos/kyoko';
import { useObservableValue } from '@talos/kyoko';
import { useMemo } from 'react';
import type { Observable } from 'rxjs';
import { of } from 'rxjs';
import { scan } from 'rxjs/operators';
import { usePinnedRowData } from './usePinnedRowData';

type BalanceMetadata = { firstEvent?: LedgerEvent; lastEvent?: LedgerEvent };

interface UseLedgerIntervalRowsOptions {
  enabled?: boolean;
}

/**
 * Calculates the first and last events in the ledger events stream and returns them as a balance metadata object.
 * @param ledgerEventsObs - The observable that emits the ledger events
 * @param enabled - Whether the interval rows should be enabled
 * @returns The pinned top and bottom row data to apply to gridOptions
 */
export function useLedgerIntervalRows(
  ledgerEventsObs: Observable<SubscriptionResponse<LedgerEvent>>,
  { enabled = false }: UseLedgerIntervalRowsOptions
) {
  const balanceObservable = useMemo(() => {
    if (!enabled) {
      return of<BalanceMetadata>({});
    }
    return ledgerEventsObs.pipe(
      scan((metadata, message) => {
        if (message.initial) {
          metadata = {} as BalanceMetadata;
        }

        if (message.data.length === 0) {
          return metadata;
        }

        if (!metadata.firstEvent) {
          metadata.firstEvent = message.data.at(0);
        }

        metadata.lastEvent = message.data.at(-1);

        return metadata;
      }, {} as BalanceMetadata)
    );
  }, [enabled, ledgerEventsObs]);

  const balanceMetadata = useObservableValue(() => balanceObservable, [balanceObservable]);

  const pinnedTopRowData = usePinnedRowData(enabled, balanceMetadata?.firstEvent, 'start');
  const pinnedBottomRowData = usePinnedRowData(enabled, balanceMetadata?.lastEvent, 'end');

  return useMemo(() => ({ pinnedTopRowData, pinnedBottomRowData }), [pinnedTopRowData, pinnedBottomRowData]);
}
